<template>
  <div class="event-shell">
    <EventBreadcrumb :items="breadcrumbItems" />

    <div v-if="isLoading" class="card loading-card">
      Memuat data event...
    </div>

    <template v-else>
      <div
        v-if="profileVerified"
        class="alert alert--success"
      >
        ✓
        <span>
          <strong>Data SIPOL Anda sudah lengkap & terverifikasi.</strong>
          Anda cukup melengkapi Foto Diri terbaru lalu menekan Submit.
        </span>
      </div>

      <div
        v-else
        class="alert alert--warning"
      >
        ⚠
        <span>
          <strong>Pastikan data Anda valid</strong> agar bisa masuk daftar calon
          penerima. Perbarui data & Foto KTP di bawah, lengkapi Foto Diri,
          lalu Submit.
        </span>
      </div>

      <!-- PROFILE SUDAH VERIFIED -->
      <section
        v-if="profileVerified"
        class="verified-layout"
        :class="{ 'verified-layout--single-column': profileHasPhoto }"
      >
        <article v-if="!profileHasPhoto" class="card selfie-card">
          <div class="section-heading">
            <h3>Foto Diri <span>*</span></h3>
            <span class="required-badge">WAJIB DIPERBARUI</span>
          </div>

          <div class="selfie-content">
            <div
              class="photo-preview"
              :class="{ 'photo-preview--empty': !selfiePreview }"
            >
              <img
                v-if="selfiePreview"
                :src="selfiePreview"
                alt="Preview foto diri"
              >
            </div>

            <div>
              <label class="btn-primary upload-button" style="display: flex; gap: 8px; align-items: center; width: max-content;">
                <svg xmlns="http://www.w3.org/2000/svg" width="1.2em" height="1.2em" viewBox="0 0 24 24">
                  <path d="M0 0h24v24H0z" fill="none" />
                  <path fill="currentColor" d="M20 4h-3.17L15 2H9L7.17 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2m0 14H4V6h4.05l1.83-2h4.24l1.83 2H20zM12 7a5 5 0 0 0-5 5a5 5 0 0 0 5 5a5 5 0 0 0 5-5a5 5 0 0 0-5-5m0 8a3 3 0 0 1-3-3a3 3 0 0 1 3-3a3 3 0 0 1 3 3a3 3 0 0 1-3 3" />
                </svg>
                Ambil / Unggah Foto Diri
                <input
                  type="file"
                  accept="image/*"
                  capture="user"
                  @change="handleSelfieUpload"
                >
              </label>

              <p class="helper">
                Foto Diri dipakai untuk mencetak KTA beserta QR Code.
                <strong>Bukan syarat SIPOL</strong> — foto buram tidak
                menggagalkan verifikasi, namun tetap wajib dilengkapi setiap
                mengambil voucher.
              </p>
            </div>
          </div>
        </article>

        <article class="card member-summary">
          <h3>Ringkasan Data Anggota</h3>

          <dl>
            <div>
              <dt>No. KTA</dt>
              <dd>{{ profileMemberNo }}</dd>
            </div>
            <div>
              <dt>NIK</dt>
              <dd>{{ profile.nik || '-' }}</dd>
            </div>
            <div>
              <dt>Nama</dt>
              <dd>{{ profile.nama_lengkap || '-' }}</dd>
            </div>
            <div>
              <dt>Kecamatan</dt>
              <dd>{{ selectedDistrictName }}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>Verified</dd>
            </div>
          </dl>

          <button
            class="btn-primary btn-full"
            :disabled="submitDisabled || isSubmitting"
            @click="submitRegistration"
          >
            <span v-if="isSubmitting">Memproses...</span>
            <span v-else>Submit Pendaftaran</span>
          </button>
        </article>
      </section>

      <!-- PROFILE BELUM VERIFIED / NULL -->
      <template v-else>
        <section class="upload-grid">
          <article class="card upload-card">
            <h3>Foto KTP</h3>

            <UploadField
              v-model="ktpFile"
              @update:modelValue="handleKtpUpload"
            />

            <p v-if="ktpFile" class="file-name">
              {{ ktpFile.name }}
            </p>
          </article>

          <article class="card selfie-card">
            <div class="section-heading">
              <h3>Foto Diri <span>*</span></h3>
              <span class="required-badge">WAJIB DIPERBARUI</span>
            </div>

            <div class="selfie-content">
              <div
                class="photo-preview"
                :class="{ 'photo-preview--empty': !selfiePreview }"
              >
                <img
                  v-if="selfiePreview"
                  :src="selfiePreview"
                  alt="Preview foto diri"
                >
              </div>

              <div>
                <label class="btn-primary upload-button">
                  ▣ &nbsp; Ambil / Unggah Foto Diri
                  <input
                    type="file"
                    accept="image/*"
                    capture="user"
                    @change="handleSelfieUpload"
                  >
                </label>

                <p class="helper">
                  Foto Diri dipakai untuk mencetak KTA beserta QR Code.
                  <strong>Bukan syarat SIPOL</strong>.
                </p>
              </div>
            </div>
          </article>
        </section>

        <section class="card form-card">
          <h3>
            Data Diri
            <small>(dapat diperbarui sendiri)</small>
          </h3>

          <div class="form-grid">
            <FieldInput
              v-model="form.nama_lengkap"
              label="Nama Lengkap"
              required
              placeholder="Masukkan nama lengkap"
            />

            <FieldInput
              v-model="form.nik"
              label="NIK"
              required
              placeholder="16 digit NIK"
              :maxlength="16"
              type="numeric"
              :state="nikState"
              :message="nikMessage"
            />

            <FieldSelect
              v-model="form.jenis_kelamin"
              label="Jenis Kelamin"
              placeholder="Pilih jenis kelamin"
              :options="jenisKelaminList"
              label-key="label"
              value-key="value"
              :searchable="false"
              required
            />

            <FieldInput
              v-model="form.tempat_lahir"
              label="Tempat Lahir"
              required
              placeholder="Masukkan tempat lahir"
            />

            <FieldDate
              v-model="form.tanggal_lahir"
              label="Tanggal Lahir"
              required
              placeholder="DD/MM/YYYY"
              :state="dobState"
              :message="dobMessage"
            />

            <FieldSelect
              v-model="form.status_id"
              label="Status Kawin"
              placeholder="Pilih status"
              :options="statusList"
              label-key="status_name"
              value-key="status_id"
              :searchable="false"
              required
            />

            <FieldSelect
              v-model="form.job_id"
              label="Pekerjaan"
              placeholder="Pilih pekerjaan"
              :options="jobList"
              label-key="job_name"
              value-key="job_id"
              required
            />

            <FieldSelect
              v-model="form.prov_id"
              label="Provinsi"
              placeholder="Pilih provinsi"
              :options="provinceList"
              label-key="prov_name"
              value-key="prov_id"
              required
              @update:modelValue="onProvinceChange"
            />

            <FieldSelect
              v-model="form.kab_id"
              label="Kota/Kabupaten"
              placeholder="Pilih kota/kabupaten"
              :options="kabList"
              label-key="kab_name"
              value-key="kab_id"
              required
              :disabled="!form.prov_id"
              @update:modelValue="onKabupatenChange"
            />

            <FieldSelect
              v-model="form.kec_id"
              label="Kecamatan"
              placeholder="Pilih kecamatan"
              :options="kecList"
              label-key="kec_name"
              value-key="kec_id"
              required
              :disabled="!form.kab_id"
              @update:modelValue="onKecamatanChange"
            />

            <FieldSelect
              v-model="form.kel_id"
              label="Kelurahan"
              placeholder="Pilih kelurahan"
              :options="kelList"
              label-key="kel_name"
              value-key="kel_id"
              required
              :disabled="!form.kec_id"
            />

            <FieldInput
              v-model="form.alamat_ktp"
              label="Alamat"
              required
              placeholder="Masukkan alamat sesuai KTP"
            />
          </div>

          <button
            class="btn-primary btn-full"
            :disabled="submitDisabled || isSubmitting"
            @click="submitRegistration"
          >
            <span v-if="isSubmitting">Memproses...</span>
            <span v-else>Submit Pendaftaran</span>
          </button>
        </section>
      </template>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import dayjs from 'dayjs'
import EventBreadcrumb from '@/components/EventBreadcrumb.vue'
import UploadField from '@/components/UploadField.vue'
import FieldInput from '@/components/FieldInput.vue'
import FieldSelect from '@/components/FieldSelect.vue'
import FieldDate from '@/components/FieldDate.vue'

export default {
  name: 'EventRegistrationPage',

  components: {
    EventBreadcrumb,
    UploadField,
    FieldInput,
    FieldSelect,
    FieldDate
  },

  data () {
    return {
      isLoading: false,
      isSubmitting: false,

      profile: {},
      selfieFile: null,
      selfiePreview: '',
      ktpFile: null,

      provinceList: [],
      kabList: [],
      kecList: [],
      kelList: [],
      jobList: [],
      statusList: [],

      jenisKelaminList: [
        { value: 'L', label: 'Laki-laki' },
        { value: 'P', label: 'Perempuan' }
      ],

      form: {
        nik: '',
        nama_lengkap: '',
        tempat_lahir: '',
        tanggal_lahir: null,
        jenis_kelamin: '',
        status_id: '',
        job_id: '',
        alamat_ktp: '',
        prov_id: '',
        kab_id: '',
        kec_id: '',
        kel_id: '',
        post_code: ''
      }
    }
  },

  computed: {
    eventId () {
      return this.$route.params.eventId
    },

    breadcrumbItems () {
      return [
        { label: 'Beranda', to: '/dashboard' },
        { label: 'Event Perindo', to: '/event-voucher' },
        { label: 'Pendaftaran' }
      ]
    },

    profileVerified () {
      const value = this.unwrapNullable(this.profile?.is_valid_ktp)

      return value
    },

    /*
     * Field foto belum tersedia pada response profile saat ini.
     * Saat backend menambahkan field, sesuaikan kandidat di bawah.
     */
    profileHasPhoto () {
      return Boolean(
        this.profile?.foto_diri_url ||
        this.profile?.profile_photo_url ||
        this.profile?.photo_url
      )
    },

    profileMemberNo () {
      return this.unwrapNullable(this.profile?.member_no) || '-'
    },

    selectedDistrictName () {
      const selected = this.kecList.find(
        item => String(item.kec_id) === String(this.form.kec_id)
      )

      return (
        selected?.kec_name ||
        this.profile?.third_level_adm_div ||
        '-'
      )
    },

    nikState () {
      if (!this.form.nik) return 'default'
      return String(this.form.nik).length === 16 ? 'default' : 'error'
    },

    nikMessage () {
      if (!this.form.nik) return ''
      return String(this.form.nik).length === 16
        ? ''
        : 'NIK harus 16 digit'
    },

    dobState () {
      if (!this.form.tanggal_lahir) return 'default'
      return this.isValidDDMMYYYY(this.form.tanggal_lahir)
        ? 'default'
        : 'error'
    },

    dobMessage () {
      if (!this.form.tanggal_lahir) return ''
      return this.isValidDDMMYYYY(this.form.tanggal_lahir)
        ? ''
        : 'Format tanggal harus DD/MM/YYYY'
    },

    isUnverifiedFormValid () {
      return Boolean(
        this.ktpFile &&
        this.selfieFile &&
        this.form.nik &&
        this.form.nama_lengkap &&
        this.form.tempat_lahir &&
        this.form.tanggal_lahir &&
        this.form.jenis_kelamin &&
        this.form.status_id &&
        this.form.job_id &&
        this.form.alamat_ktp &&
        this.form.prov_id &&
        this.form.kab_id &&
        this.form.kec_id &&
        this.form.kel_id
      )
    },

    submitDisabled () {
      if (this.profileVerified) {
        return !this.profileHasPhoto && !this.selfieFile
      }

      return !this.isUnverifiedFormValid
    }
  },

  methods: {
    unwrapNullable (value) {
      if (value === null || value === undefined) return null

      if (typeof value !== 'object') return value

      if ('Valid' in value && value.Valid === false) return null
      if ('String' in value) return value.String
      if ('Int32' in value) return value.Int32
      if ('Int64' in value) return value.Int64
      if ('Bool' in value) return value.Bool

      return value
    },

    normalizeProfile (rawProfile) {
      return {
        ...rawProfile,
        member_no: this.unwrapNullable(rawProfile.member_no),
        prov_id_domisili: this.unwrapNullable(rawProfile.prov_id_domisili),
        kab_id_domisili: this.unwrapNullable(rawProfile.kab_id_domisili),
        kec_id_domisili: this.unwrapNullable(rawProfile.kec_id_domisili),
        kel_id_domisili: this.unwrapNullable(rawProfile.kel_id_domisili),
        alamat_domisili: this.unwrapNullable(rawProfile.alamat_domisili),
        negara_domisili: this.unwrapNullable(rawProfile.negara_domisili)
      }
    },

    setProfileToForm () {
      const profile = this.profile

      this.form.nik = profile.nik || ''
      this.form.nama_lengkap = profile.nama_lengkap || ''
      this.form.tempat_lahir = profile.tempat_lahir || ''
      this.form.tanggal_lahir = profile.tanggal_lahir
        ? dayjs(profile.tanggal_lahir).format('DD/MM/YYYY')
        : null
      this.form.jenis_kelamin = profile.jenis_kelamin || ''
      this.form.status_id = profile.status_id || ''
      this.form.job_id = profile.job_id || ''
      this.form.alamat_ktp = profile.alamat_ktp || ''
      this.form.prov_id = profile.prov_id || ''
      this.form.kab_id = profile.kab_id || ''
      this.form.kec_id = profile.kec_id || ''
      this.form.kel_id = profile.kel_id || ''
      this.form.post_code = profile.post_code || ''
    },

    async getProfile () {
      const { data: dataProfil } = await axios.get('member/member')

      const rawProfile = dataProfil
      this.profile = this.normalizeProfile(rawProfile)
      this.setProfileToForm()
    },

    async loadProfileRegionOptions () {
      if (this.form.prov_id) {
        await this.getKabupaten(this.form.prov_id)
      }

      if (this.form.kab_id) {
        await this.getKecamatan(this.form.kab_id)
      }

      if (this.form.kec_id) {
        await this.getKelurahan(this.form.kec_id)
      }
    },

    async selectProv () {
      const response = await axios.get('member/listprovinsi')
      this.provinceList = response.data || []
    },

    async getKabupaten (provId) {
      const response = await axios.get(
        `member/listkabupatenkota?prov_id=${provId}`
      )
      this.kabList = response.data || []
    },

    async getKecamatan (kabId) {
      const response = await axios.get(
        `member/listkecamatan?kab_id=${kabId}`
      )
      this.kecList = response.data || []
    },

    async getKelurahan (kecId) {
      const response = await axios.get(
        `member/listkelurahandesa?kec_id=${kecId}`
      )
      this.kelList = response.data || []
    },

    async getJobList () {
      const response = await axios.get('member/listjob')
      this.jobList = response.data || []
    },

    async getStatusList () {
      const response = await axios.get('member/liststatus')
      this.statusList = response.data || []
    },

    async onProvinceChange (provId) {
      this.form.kab_id = ''
      this.form.kec_id = ''
      this.form.kel_id = ''
      this.kabList = []
      this.kecList = []
      this.kelList = []

      if (provId) {
        await this.getKabupaten(provId)
      }
    },

    async onKabupatenChange (kabId) {
      this.form.kec_id = ''
      this.form.kel_id = ''
      this.kecList = []
      this.kelList = []

      if (kabId) {
        await this.getKecamatan(kabId)
      }
    },

    async onKecamatanChange (kecId) {
      this.form.kel_id = ''
      this.kelList = []

      if (kecId) {
        await this.getKelurahan(kecId)
      }
    },

    handleKtpUpload (file) {
      this.ktpFile = file || null
    },

    handleSelfieUpload (event) {
      const file = event.target.files?.[0] || null
      this.selfieFile = file

      if (this.selfiePreview) {
        URL.revokeObjectURL(this.selfiePreview)
      }

      this.selfiePreview = file
        ? URL.createObjectURL(file)
        : ''
    },

    buildAccountPayload () {
      const payload = new FormData()

      payload.append('nama_lengkap', this.form.nama_lengkap)

      if (String(this.form.nik) !== String(this.profile.nik || '')) {
        payload.append('nik', this.form.nik)
      }

      payload.append('tempat_lahir', this.form.tempat_lahir)
      payload.append(
        'tanggal_lahir',
        this.convertDateToISO(this.form.tanggal_lahir)
      )
      payload.append('jenis_kelamin', this.form.jenis_kelamin)
      payload.append('status_id', this.form.status_id)
      payload.append('job_id', this.form.job_id)

      return payload
    },

    buildAddressPayload () {
      const payload = new FormData()

      payload.append('alamat_ktp', this.form.alamat_ktp)
      payload.append('prov_id', this.form.prov_id)
      payload.append('kab_id', this.form.kab_id)
      payload.append('kec_id', this.form.kec_id)
      payload.append('kel_id', this.form.kel_id)

      return payload
    },

    buildDocumentPayload () {
      const payload = new FormData()

      payload.append('foto_ktp', this.ktpFile)

      return payload
    },

    buildEventRegistrationPayload () {
      const payload = new FormData()

      if (this.selfieFile) {
        payload.append('image', this.selfieFile)
      }

      return payload
    },

    async updateUnverifiedProfile () {
      await Promise.all([
        axios.patch('member/member/account', this.buildAccountPayload()),
        axios.patch('member/member/address', this.buildAddressPayload()),
        //axios.patch('member/member/document', this.buildDocumentPayload())
      ])
    },

    async registerEvent () {
      await axios.post(
        `member/event-vouchers/${this.eventId}/register`,
        this.buildEventRegistrationPayload()
      )
    },

    async submitRegistration () {
      if (this.submitDisabled || this.isSubmitting) return

      let failedAction = 'melakukan pendaftaran event'

      try {
        this.isSubmitting = true

        if (!this.profileVerified) {
          failedAction = 'memperbarui data profil'
          await this.updateUnverifiedProfile()
        }

        failedAction = 'melakukan pendaftaran event'
        await this.registerEvent()

        this.$router.replace({
          name: 'event-detail',
          params: {
            id: this.eventId
          },
          query: {
            view: 'success'
          }
        })
      } catch (err) {
        const message =
          err.response?.data?.message ||
          `Gagal ${failedAction}`

        this.$store.commit('showGlobalAlert', {
          message,
          type: 'danger'
        })
      } finally {
        this.isSubmitting = false
      }
    },

    convertDateToISO (dateStr) {
      if (!dateStr) return ''
      const [day, month, year] = String(dateStr).split('/')
      return `${year}-${month}-${day}`
    },

    isValidDDMMYYYY (value) {
      if (!/^(\d{2})\/(\d{2})\/(\d{4})$/.test(value)) return false

      const [day, month, year] = value.split('/').map(Number)
      const date = new Date(year, month - 1, day)

      return (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      )
    }
  },

  async mounted () {
    this.isLoading = true

    try {
      await Promise.all([
        this.getProfile(),
        this.selectProv(),
        this.getJobList(),
        this.getStatusList()
      ])

      await this.loadProfileRegionOptions()
    } catch (err) {
      const message =
        err.response?.data?.message ||
        'Gagal memuat data pendaftaran'

      this.$store.commit('showGlobalAlert', {
        message,
        type: 'danger'
      })
    } finally {
      this.isLoading = false
    }
  },

  beforeUnmount () {
    if (this.selfiePreview) {
      URL.revokeObjectURL(this.selfiePreview)
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.event-shell {
  min-height: calc(100vh - 60px);
  color: #172852;
}

.card {
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(20, 37, 80, .04);
}

.loading-card {
  padding: 36px;
  text-align: center;
}

.alert {
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
  padding: 15px 18px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.5;
}

.alert--success {
  background: #e8f8ee;
  color: #25713f;
}

.alert--warning {
  background: #fff3d8;
  color: #946a18;
}

.verified-layout,
.upload-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  align-items: start;
}

.selfie-card {
  align-self: start;
  height: max-content;
  padding: 20px;
}

.verified-layout--single-column {
  grid-template-columns: minmax(360px, 520px);
  justify-content: center;
}

.member-summary,
.upload-card,
.form-card {
  padding: 20px;
}

.section-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.section-heading h3,
.member-summary h3,
.upload-card h3,
.form-card h3 {
  margin-top: 0;
}

.section-heading h3 span {
  color: #df3747;
}

.required-badge {
  height: fit-content;
  padding: 7px 11px;
  border-radius: 999px;
  background: #fff1c9;
  color: #9b6711;
  font-size: 10px;
  font-weight: 800;
}

.selfie-content {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 14px;
  align-items: center;
}

.photo-preview {
  width: 100px;
  height: 105px;
  overflow: hidden;
  border-radius: 12px;
  background: #edf1f7;
}

.photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-preview--empty {
  background:
    radial-gradient(circle at 50% 38%, #b8c2d3 0 20%, transparent 21%),
    linear-gradient(to top, #b8c2d3 0 45%, #edf1f7 46%);
}

.upload-button {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.upload-button input {
  display: none;
}

.helper {
  margin: 10px 0 0;
  color: #777f8e;
  font-size: 11px;
  line-height: 1.55;
}

.member-summary dl {
  margin: 0 0 16px;
}

.member-summary dl > div {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 14px;
  padding: 13px 0;
  border-bottom: 1px dashed #dfe4ec;
}

dt {
  color: #7a8394;
}

dd {
  margin: 0;
  color: #182956;
  font-weight: 800;
  text-align: right;
  word-break: break-word;
}

.form-card {
  margin-top: 18px;
}

.form-card h3 small {
  font-weight: 400;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  margin-bottom: 18px;
}

.file-name {
  margin: 10px 0 0;
  color: #687386;
  font-size: 12px;
}

.btn-primary {
  min-height: 42px;
  padding: 0 18px;
  border: 0;
  border-radius: 9px;
  background: #192e62;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
}

.btn-primary:disabled {
  background: #c8cfdd;
  cursor: not-allowed;
}

.btn-full {
  width: 100%;
}

@media (max-width: 900px) {
  .verified-layout,
  .verified-layout--single-column,
  .upload-grid {
    grid-template-columns: 1fr;
  }

  .form-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .event-shell {
    padding: 12px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .selfie-card,
  .member-summary,
  .upload-card,
  .form-card {
    padding: 14px;
  }

  .selfie-content {
    grid-template-columns: 80px 1fr;
  }

  .photo-preview {
    width: 80px;
    height: 92px;
  }
}
</style>
